(ns opengb.i18n.re-frame
  (:require
   [opengb.i18n.locale :as i18n.locale]
   [opengb.i18n.m1p    :as i18n.m1p]
   [opengb.i18n.sort   :as i18n.sort]
   [re-frame.core      :as rf]
   [taoensso.timbre    :as timbre]))

(defn initialize-i18n
  "Registers re-frame events, subscriptions, and cofx to handle browser
  locale, translation config, and debugging. 

  app-db needs :opengb.i18n/dictionaries to contain a map of locales and
  m1p dictionaries for all of this to function."
  []
  ;; # Locales

  ;; When present, contains a string representing the user's
  ;; most preferred locale.
  ;; See https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language
  (rf/reg-cofx :opengb.i18n/language
    (fn [cofx]
      (assoc cofx :opengb.i18n/language (.-language js/navigator))))

  ;; When present, contains a list of strings representing the user's
  ;; preferred locales.
  ;; See https://developer.mozilla.org/en-US/docs/Web/API/Navigator/languages
  (rf/reg-cofx
    :opengb.i18n/languages
    (fn [cofx]
      (assoc cofx :opengb.i18n/languages (set (.-languages js/navigator)))))

  (rf/reg-event-fx
    :opengb.i18n/load-user-locale
    [(rf/inject-cofx :opengb.i18n/languages)]
    (fn [{raw-languages :opengb.i18n/languages :keys [db]} _]
      (let [languages         (map keyword raw-languages)
            available-locales (keys (:opengb.i18n/dictionaries db))
            locale            (i18n.locale/select-locale available-locales languages)]
        (when (not-any? (partial = locale) languages)
          (timbre/warn "User locales"
                       languages
                       "not available, using"
                       (name locale)
                       "as fallback"))
        {:dispatch [:opengb.i18n/set-locale locale]})))

  (rf/reg-event-db :opengb.i18n/set-locale
    (fn [db [_ locale]] (assoc db :opengb.i18n/locale locale)))

  (rf/reg-event-db :opengb.i18n/toggle-locale
    (fn [db _]
      (let [current-locale (:opengb.i18n/locale db)]
        (if (= current-locale :en-CA)
          (assoc db :opengb.i18n/locale :fr-CA)
          (assoc db :opengb.i18n/locale :en-CA)))))

  (rf/reg-sub :opengb.i18n/locale (fn [db _] (:opengb.i18n/locale db)))

  ;; # Debugging

  (rf/reg-event-db :opengb.i18n/toggle-debug
    (fn [db _] (update db :opengb.i18n/debug? not)))

  (rf/reg-sub :opengb.i18n/debug? (fn [db _] (:opengb.i18n/debug? db)))

  ;; # Translation

  (rf/reg-sub :opengb.i18n/dictionaries (fn [db _] (:opengb.i18n/dictionaries db)))

  (rf/reg-sub :opengb.i18n/translation-config
    (fn []
      {:m1p-dictionaries (rf/subscribe [:opengb.i18n/dictionaries])
       :locale (rf/subscribe [:opengb.i18n/locale])
       :debug? (rf/subscribe [:opengb.i18n/debug?])})
    (fn [config _]
      (update config :m1p-dictionaries i18n.m1p/prepare-dictionaries))))

(defn translate-rf
  "Translation function that returns a string. Takes in a key to translate and an
  optional map of options. Uses :opengb.i18n/translation-config to get
  dictionaries, current locale, and debug flag. 

  In normal mode, if translation succeeds the correct string is returned.
  If a :default-str has been provided in the params it will be displayed next.
  Otherwise the translation key as a string is returned. 

  In debug mode, a correctly translated string is returned with ✓
  appended, while a failed translation prints the error to the console and
  returns the error as a string surrounded by ¡¡¡!!!."
  ([k] (translate-rf k nil))
  ([k params]
   (let [translation-config @(rf/subscribe [:opengb.i18n/translation-config])
         {:keys [m1p-dictionaries locale debug?]} translation-config]
     (when m1p-dictionaries
       (if debug?
         (let [{:keys [translated-str error input]}
               (i18n.m1p/translate m1p-dictionaries locale k params debug?)]
           (if translated-str
             (str translated-str " ✓")
             (do (timbre/debug "Translate Error" error "for input" input)
                 (str "¡¡¡" error "!!!"))))
         (let [translated-str (i18n.m1p/translate m1p-dictionaries locale k params debug?)
               translation-failed? (= translated-str (str k))
               {:keys [default-str]} params]
           (if (and translation-failed? default-str)
             default-str
             translated-str)))))))

(defn TranslatedSpan
  "Reagent component that provides a translated span. Takes in a key to
  translate and an optional map of options. Uses
  :opengb.i18n/translation-config to get dictionaries, current locale, and
  debug flag. 

  In normal mode, if translation succeeds the correct string is displayed.
  If a :default-str has been provided in the params it will be displayed next.
  Otherwise the translation key as a string is displayed.

  If :html? has been set to true in params then the translated string will be
  set as the inner html of the span.

  In debug mode, a correctly translated string is returned with ✓
  displayed. A failed translation displays the key as a string in hot pink
  and underlined. The full error is displayed in a hover tooltip."
  ([k] (TranslatedSpan k nil))
  ([_k _params]
   (let [*translation-config (rf/subscribe [:opengb.i18n/translation-config])]
     (fn [k {:keys [default-str html?] :as params}]
       (let [{:keys [m1p-dictionaries locale debug?]} @*translation-config]
         (when m1p-dictionaries
           (if debug?
             (let [{:keys [translated-str error input]}
                   (i18n.m1p/translate m1p-dictionaries locale k params debug?)]
               (cond (and translated-str (not html?))
                     [:span {:title (str "Translation key: " k)
                             :class "(and translated-str (not html?))"} translated-str " ✓"]
                     (and translated-str html?)
                     [:span {:class "(and translated-str html?)"}
                      [:span {:title (str "Translation key: " k)
                              :dangerouslySetInnerHTML {:__html translated-str}}] " ✓"]
                     :else [:span {:style {:text-decoration "underline" :color "#ff00ff"}
                                   :class ":else"
                                   :title (str error " for input " input)}
                            (str k)]))
             (let [translated-str (i18n.m1p/translate m1p-dictionaries locale k params debug?)
                   translation-failed? (= translated-str (str k))
                   output (if (and translation-failed? default-str)
                            default-str
                            translated-str)]
               (if html?
                 [:span {:dangerouslySetInnerHTML {:__html output}}]
                 [:span output])))))))))

(defn make-locale-comparator
  "Gets current :opengb.i18n/locale and returns a comparator for that locale."
  []
  (let [*locale (rf/subscribe [:opengb.i18n/locale])]
    (i18n.sort/make-locale-comparator {:opengb.i18n/locale @*locale})))
